import React, { useEffect, useState } from "react";

/**
 * Brass scroll-progress strip at the very top of the page. Thin (2px),
 * fills left-to-right with brass as the reader moves down the page.
 * The whole strip sits inside a 1px ink hairline so the empty state
 * still reads as deliberate rather than missing.
 *
 * Throttled to requestAnimationFrame so it never thrashes scroll perf.
 */
export default function ScrollProgress() {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let pending = false;
    const update = () => {
      pending = false;
      const doc = document.documentElement;
      const max = (doc.scrollHeight || 0) - (window.innerHeight || 0);
      const cur = window.scrollY || doc.scrollTop || 0;
      const next = max > 0 ? Math.min(1, Math.max(0, cur / max)) : 0;
      setPct(next);
    };
    const onScroll = () => {
      if (!pending) {
        pending = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
      style={{
        height: 2,
        background: "rgba(26, 31, 54, 0.06)",
        borderBottom: "1px solid rgba(26, 31, 54, 0.08)",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${(pct * 100).toFixed(2)}%`,
          background: "linear-gradient(to right, var(--brass), var(--vermilion))",
          transition: "width 80ms linear",
        }}
      />
    </div>
  );
}
